import PageLayout from "@base/layouts/PageLayout";
import ProtectedRoute from "@base/routes/ProtectedRoute";
import { baseRoutes } from "./routePaths";
import Home from "@pages/home";
import Login from "@pages/login";
import User from "@pages/user";
import Appointment from "@pages/user/appointment";
import Examination from "@pages/user/examination";
import ServicePackage from "@pages/user/package";
import PpaymentHistory from "@pages/user/payment";
import Prescription from "@pages/user/prescription";
import Profile from "@pages/user/profile";
import Promotion from "@pages/user/promotion";
import Partner from "@pages/partner";
import PolicyActivation from "@pages/policyActivation";
import { useRoutes } from "react-router";
import { Navigate } from "react-router-dom";
import ProfileList from "@pages/user/profile/profile-list";
import ProfileHandler from "@pages/user/profile/profile-handler";
import AppointmentList from "@pages/user/appointment/appointment-list";
import AppointmentDetail from "@pages/user/appointment/appointment-detail";
import AccountSetting from "@pages/user/account";
import MedicalInfo from "@pages/medicalInfo";
import MedicalInfoDetail from "@pages/medicalInfo/[id]";
import Search from "@pages/search";
import OrderFacility from "@pages/orderFacility";
import OrderFacilityDetail from "@pages/orderFacility/[id]";
import Doctor from "@pages/doctor";
import DoctorDetail from "@pages/doctor/[id]";
import Specialist from "@pages/specialist";
import SpecialistDetail from "@pages/specialist/[id]";
import SiteListForSync from "@components/User/Profile/SiteListForSync/SiteListForSync";
import AppointmentBooking from "@pages/appointmentBooking";
import BookingPackage from "@pages/bookingPackage";
import PackageDetail from "@pages/bookingPackage/[id]";
import PaymentPackageService from "@components/PaymentPackageService";
import BookingOnline from "@pages/bookingOnline";
import Medicine from "@pages/medicine";
import Result from "@pages/order/Result";
import ChangePassword from "@pages/user/changePassword";
import ServicePackageList from "@pages/user/package/package-list";
import ServicePackageDetail from "@pages/user/package/package-detail";
import PackageBooking from "@pages/user/package/package-booking";
import PaymentList from "@pages/user/payment/payment-list";
import PaymentDetail from "@pages/user/payment/payment-detail";
import { useLocation } from "react-router-dom";
import { useEffect } from "react";
import { backToTop } from "@base/helpers/common";
import PatientChronic from "@pages/user/chronic";
import UserInsurance from "@pages/user/insurance";
import PrescriptionList from "@pages/user/prescription/prescription-list";
import PrescriptionDetail from "@pages/user/prescription/prescription-detail";
import Privacy from "@pages/privacy";
import About from "@pages/About";
import AchievementsAndAwards from "@pages/AchievementsAndAwards";
import ProductInfo from "@pages/products";

// Kept as a re-export so the many `import { baseRoutes } from "@base/routes/baseRoutes"` callers
// keep working; new code can import from "@base/routes/routePaths" to avoid the route tree.
export { baseRoutes } from "./routePaths";

export const AppRoutes = () => {
  const location = useLocation();

  useEffect(() => {
    backToTop();
  }, [location.pathname]);

  const routes = useRoutes([
    {
      path: baseRoutes.home,
      element: <PageLayout />,
      children: [
        { index: true, element: <Home /> },
        { path: baseRoutes.login, element: <Login /> },
        // User - everything below requires a session
        {
          element: <ProtectedRoute />,
          children: [
            {
              path: baseRoutes.user,
              element: <User />,
              children: [
                { index: true, element: <Navigate to={baseRoutes.userAccount} replace /> },
                { path: baseRoutes.userAccount, element: <AccountSetting /> },
                {
                  path: baseRoutes.userProfile,
                  element: <Profile />,
                  children: [
                    { index: true, element: <Navigate to={baseRoutes.userProfileList} replace /> },
                    { path: baseRoutes.userProfileList, element: <ProfileList /> },
                    { path: baseRoutes.userProfileHandler, element: <ProfileHandler /> },
                    { path: baseRoutes.userProfileSync, element: <SiteListForSync /> },
                  ],
                },
                {
                  path: baseRoutes.userAppointment,
                  element: <Appointment />,
                  children: [
                    { index: true, element: <Navigate to={baseRoutes.userAppointmentList} replace /> },
                    { path: baseRoutes.userAppointmentList, element: <AppointmentList /> },
                    { path: baseRoutes.userAppointmentDetail, element: <AppointmentDetail /> },
                  ],
                },
                {
                  path: baseRoutes.userPrescription,
                  element: <Prescription />,
                  children: [
                    { index: true, element: <Navigate to={baseRoutes.userPrescriptionList} replace /> },
                    { path: baseRoutes.userPrescriptionList, element: <PrescriptionList /> },
                    { path: baseRoutes.userPrescriptionDetail, element: <PrescriptionDetail /> },
                  ],
                },
                {
                  path: baseRoutes.userPackage,
                  element: <ServicePackage />,
                  children: [
                    { index: true, element: <Navigate to={baseRoutes.userPackageList} replace /> },
                    { path: baseRoutes.userPackageList, element: <ServicePackageList /> },
                    { path: baseRoutes.userPackageDetail, element: <ServicePackageDetail /> },
                    { path: baseRoutes.userPackageBooking, element: <PackageBooking /> },
                  ],
                },
                { path: baseRoutes.userPromotion, element: <Promotion /> },
                { path: baseRoutes.userExamination, element: <Examination /> },
                { path: baseRoutes.userChronic, element: <PatientChronic /> },
                { path: baseRoutes.userInsurance, element: <UserInsurance /> },
                {
                  path: baseRoutes.userPayment,
                  element: <PpaymentHistory />,
                  children: [
                    { index: true, element: <Navigate to={baseRoutes.userPaymentList} replace /> },
                    { path: baseRoutes.userPaymentList, element: <PaymentList /> },
                    { path: baseRoutes.userPaymentDetail, element: <PaymentDetail /> },
                  ],
                },
                { path: baseRoutes.userChangePassword, element: <ChangePassword /> },
              ],
            },
          ],
        },
        // Default
        { path: baseRoutes.medicine, element: <Medicine /> },
        {
          path: baseRoutes.medicalInfo,
          children: [
            { index: true, element: <MedicalInfo /> },
            { path: ":slug", element: <MedicalInfoDetail /> },
          ],
        },
        {
          path: baseRoutes.product,
          children: [{ path: ":type", element: <ProductInfo /> }],
        },
        { path: baseRoutes.partner, element: <Partner /> },
        // Public on purpose: the whole point is that these customers have no account yet.
        { path: baseRoutes.policyActivation, element: <PolicyActivation /> },
        { path: baseRoutes.search, element: <Search /> },
        {
          path: baseRoutes.orderFacility,
          children: [
            { index: true, element: <OrderFacility /> },
            { path: ":slug", element: <OrderFacilityDetail /> },
          ],
        },
        {
          path: baseRoutes.bookingOnline,
          children: [
            { index: true, element: <BookingOnline /> },
            { path: ":id", element: <OrderFacilityDetail /> },
          ],
        },
        {
          path: baseRoutes.doctor,
          children: [
            { index: true, element: <Doctor /> },
            { path: ":slug", element: <DoctorDetail /> },
          ],
        },
        {
          path: baseRoutes.specialist,
          children: [
            { index: true, element: <Specialist /> },
            { path: ":id", element: <SpecialistDetail /> },
          ],
        },
        {
          path: baseRoutes.bookingPackage,
          children: [
            { index: true, element: <BookingPackage /> },
            {
              path: ":slug",
              element: <PackageDetail />,
            },
            {
              path: `:id${baseRoutes.paymentPackageService}`,
              element: <PaymentPackageService />,
            },
          ],
        },
        {
          path: baseRoutes.bookingAppointment,
          element: <AppointmentBooking />,
        },
        {
          path: baseRoutes.orderResult,
          element: <Result />,
        },
        {
          path: baseRoutes.aboutUS,
          element: <About />,
        },
        {
          path: baseRoutes.privacy,
          element: <Privacy />,
        },
        {
          path: baseRoutes.paymentTerms,
          element: <Privacy />,
        },
        {
          path: baseRoutes.achievementsAndAwards,
          element: <AchievementsAndAwards />,
        },
        { path: "*", element: <Home /> },
      ],
    },
  ]);
  return routes;
};
